import { animate, motion, useMotionValue } from 'framer-motion'
import * as React from 'react'

import { cn } from '@/lib/utils'

type HoldConfirmButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'onClick' | 'type'
> & {
  type?: 'button' | 'submit'
  duration?: number
  holdingLabel?: React.ReactNode
  confirmedLabel?: React.ReactNode
  onConfirm?: () => void
}

const HoldConfirmButton = React.forwardRef<HTMLButtonElement, HoldConfirmButtonProps>(
  (
    {
      type = 'button',
      duration = 900,
      holdingLabel = 'Keep holding…',
      confirmedLabel = 'Confirmed',
      onConfirm,
      className,
      children = 'Hold to confirm',
      disabled,
      onKeyDown,
      onKeyUp,
      ...props
    },
    forwardedRef
  ) => {
    const buttonRef = React.useRef<HTMLButtonElement>(null)
    const [status, setStatus] = React.useState<'idle' | 'holding' | 'confirmed'>('idle')
    const progress = useMotionValue(0)
    const animation = React.useRef<ReturnType<typeof animate> | null>(null)

    React.useImperativeHandle(forwardedRef, () => buttonRef.current as HTMLButtonElement)
    React.useEffect(() => () => animation.current?.stop(), [])

    const cancel = () => {
      if (status !== 'holding') return
      animation.current?.stop()
      setStatus('idle')
      animate(progress, 0, { type: 'spring', stiffness: 320, damping: 32 })
    }

    const confirm = () => {
      setStatus('confirmed')
      onConfirm?.()
      if (type === 'submit') buttonRef.current?.form?.requestSubmit()
      window.setTimeout(() => {
        setStatus('idle')
        animate(progress, 0, { duration: 0.2 })
      }, 900)
    }

    const start = () => {
      if (status !== 'idle' || disabled) return
      setStatus('holding')
      animation.current = animate(progress, 1, {
        duration: duration / 1000,
        ease: 'linear',
        onComplete: confirm,
      })
    }

    const labels = { idle: children, holding: holdingLabel, confirmed: confirmedLabel }
    return (
      <button
        ref={buttonRef}
        type="button"
        data-status={status}
        disabled={disabled}
        onPointerDown={start}
        onPointerUp={cancel}
        onPointerLeave={cancel}
        onPointerCancel={cancel}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
            event.preventDefault()
            start()
          }
        }}
        onKeyUp={(event) => {
          onKeyUp?.(event)
          if (event.key === ' ' || event.key === 'Enter') cancel()
        }}
        className={cn(
          'relative inline-flex min-h-9 cursor-pointer select-none items-center justify-center overflow-hidden rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm outline-none transition-transform active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      >
        <motion.span
          aria-hidden="true"
          style={{ scaleX: progress, transformOrigin: '0% 50%' }}
          className="absolute inset-0 bg-brand-purple/75"
        />
        <span className="relative grid">
          {Object.entries(labels).map(([key, label]) => (
            <span
              key={key}
              className={cn(
                'col-start-1 row-start-1 whitespace-nowrap',
                status === key ? '' : 'invisible'
              )}
            >
              {label}
            </span>
          ))}
        </span>
      </button>
    )
  }
)
HoldConfirmButton.displayName = 'HoldConfirmButton'

export { HoldConfirmButton }
